import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import { 
  ShieldWarning, 
  User, 
  Warning, 
  SpinnerGap,
  Clock,
  Buildings
} from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * SupportAccessModal - Platform admin impersonation modal
 * 
 * Allows platform admins to securely impersonate contractor/technician
 * users for support purposes with full audit trail.
 */
export default function SupportAccessModal({ 
  open, 
  onOpenChange, 
  tenant,
  onSessionStarted 
}) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [reason, setReason] = useState("");
  const [starting, setStarting] = useState(false);
  const [sessionExpiry, setSessionExpiry] = useState(10);

  // Fetch users when modal opens
  useEffect(() => {
    if (open && tenant?.tenant_id) {
      fetchTenantUsers();
    } else {
      // Reset state when closed
      setUsers([]);
      setSelectedUser(null);
      setReason("");
    }
  }, [open, tenant?.tenant_id]);

  const fetchTenantUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API}/admin/support-access/tenant/${tenant.tenant_id}/users`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
        setSessionExpiry(data.session_expiry_minutes || 10);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to fetch users");
        onOpenChange(false);
      }
    } catch (error) {
      toast.error("Failed to fetch tenant users");
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  const handleStartSession = async () => {
    if (!selectedUser || !reason.trim() || reason.trim().length < 5) {
      toast.error("Please select a user and provide a reason (min 5 characters)");
      return;
    }

    setStarting(true);
    try {
      const response = await fetch(`${API}/admin/support-access/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          target_user_id: selectedUser.user_id,
          reason: reason.trim()
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Support session started as ${data.support_session.target_email}`);
        onOpenChange(false);
        
        // CRITICAL: Update auth identity BEFORE redirect to prevent mismatch detection
        // The authSignal system tracks identity - we need to tell it the new identity is intentional
        try {
          const newIdentity = {
            email: data.support_session.target_email,
            role: data.support_session.target_role,
            is_support_session: true
          };
          localStorage.setItem('fsp_auth_identity', JSON.stringify(newIdentity));
          // Also store support session marker
          localStorage.setItem('fsp_support_session', JSON.stringify({
            admin_email: data.support_session.admin_email,
            target_email: data.support_session.target_email,
            tenant_name: data.support_session.tenant_name,
            started_at: new Date().toISOString()
          }));
        } catch (e) {
          console.warn('[SupportAccess] Failed to update auth identity:', e);
        }
        
        // Callback to refresh page/navigate
        if (onSessionStarted) {
          onSessionStarted(data.support_session);
        } else {
          // Navigate to dashboard of the impersonated user
          window.location.href = "/dashboard";
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to start support session");
      }
    } catch (error) {
      toast.error("Failed to start support session");
    } finally {
      setStarting(false);
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      tenant_owner: "bg-purple-500/20 text-purple-400 border-purple-500/30",
      contractor: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      technician: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
      administrator: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    };
    const labels = {
      tenant_owner: "Owner",
      contractor: "Contractor",
      technician: "Technician",
      administrator: "Admin",
    };
    return {
      className: styles[role] || "bg-zinc-500/20 text-zinc-400 border-zinc-500/30",
      label: labels[role] || role
    };
  };

  const canConfirm = selectedUser && reason.trim().length >= 5;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-amber-500/50 border-2 max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-400">
            <ShieldWarning size={24} weight="fill" />
            Support Access
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Enter a support session as a user in this tenant
          </DialogDescription>
        </DialogHeader>

        {/* Warning Banner */}
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <Warning size={20} className="text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="text-amber-300 font-medium">This action is fully audited</p>
              <p className="text-amber-400/80 mt-1">
                Your access will be logged with timestamp, reason, and IP address.
                Session expires after {sessionExpiry} minutes.
              </p>
            </div>
          </div>
        </div>

        {/* Tenant Info */}
        <div className="bg-zinc-800 rounded-lg p-3 flex items-center gap-3">
          <Buildings size={20} className="text-zinc-400" />
          <div>
            <p className="text-white font-medium">{tenant?.company_name || "Unknown Tenant"}</p>
            <p className="text-xs text-zinc-500 font-mono">{tenant?.tenant_id}</p>
          </div>
        </div>

        {/* User Selection */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-zinc-300">Select User to Access</label>
          
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <SpinnerGap size={24} className="animate-spin text-zinc-400" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-6 bg-zinc-800 rounded-lg">
              <p className="text-zinc-400">No accessible users in this tenant</p>
            </div>
          ) : (
            <div className="max-h-48 overflow-y-auto space-y-1 bg-zinc-800/50 rounded-lg p-2">
              {users.map((user) => {
                const roleBadge = getRoleBadge(user.role);
                const isSelected = selectedUser?.user_id === user.user_id;
                
                return (
                  <button
                    key={user.user_id}
                    onClick={() => setSelectedUser(user)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                      isSelected 
                        ? "bg-amber-500/20 border border-amber-500/50" 
                        : "bg-zinc-800 hover:bg-zinc-700 border border-transparent"
                    }`}
                    data-testid={`support-user-${user.user_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <User size={18} className={isSelected ? "text-amber-400" : "text-zinc-400"} />
                      <div className="text-left">
                        <p className={`text-sm font-medium ${isSelected ? "text-amber-300" : "text-white"}`}>
                          {user.name || user.email}
                        </p>
                        <p className="text-xs text-zinc-500">{user.email}</p>
                      </div>
                    </div>
                    <Badge variant="outline" className={roleBadge.className}>
                      {roleBadge.label}
                    </Badge>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Reason Input */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-zinc-300">
            Reason for Access <span className="text-red-400">*</span>
          </label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g., Investigating invoice display issue reported by customer..."
            className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 min-h-[80px]"
            data-testid="support-reason-input"
          />
          <p className="text-xs text-zinc-500">
            Minimum 5 characters. This will be recorded in the audit log.
          </p>
        </div>

        {/* Session Info */}
        <div className="flex items-center gap-2 text-sm text-zinc-400">
          <Clock size={16} />
          <span>Session will expire in {sessionExpiry} minutes</span>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            data-testid="support-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleStartSession}
            disabled={!canConfirm || starting}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="support-confirm-btn"
          >
            {starting ? (
              <>
                <SpinnerGap size={16} className="animate-spin mr-2" />
                Starting...
              </>
            ) : (
              "Start Support Session"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
